import { toast } from 'react-hot-toast';

export const toYMD = (v) => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

export const parseDecimal = (v) => {
  if (v === '' || v == null) return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isNaN(n) ? null : n;
};

export const formatApiError = (data) => {
  if (!data) return 'Failed to save.';
  if (typeof data === 'string') return data;
  if (data.detail) {
    const d = data.detail;
    return Array.isArray(d) ? d.join(' ') : String(d);
  }
  const parts = [];
  Object.keys(data).forEach((k) => {
    const v = data[k];
    if (Array.isArray(v)) parts.push(...v.map((x) => String(x)));
    else if (v != null) parts.push(String(v));
  });
  return parts.length ? parts.join(' ') : 'Failed to save.';
};

export const marketSourceToString = (ms) => {
  if (ms == null) return '';
  if (typeof ms === 'string') return ms;
  try {
    return JSON.stringify(ms, null, 2);
  } catch (_) {
    return '{}';
  }
};

let rowIdSeq = 0;
export const nextRowKey = () => {
  rowIdSeq += 1;
  return `row-${rowIdSeq}`;
};

export const emptyRow = () => ({
  key: nextRowKey(),
  asset: '',
  snapshot_source: 'manually',
  snapshot_value: '',
  snapshot_quantity: '1',
  market_unitary_value: '',
  market_source_json: '',
});

export const fmtNum = (v, decimals = 2) => {
  if (v === '' || v == null) return '—';
  const n = parseFloat(v);
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/** Market unit / source apply when asset explicitly has is_updated === false → omit in UI and payload. */
export const isAssetMarketFieldsApplicable = (asset) =>
  asset == null || asset.is_updated !== false;

export const rowFromItem = (item) => ({
  key: nextRowKey(),
  asset: item.asset != null ? String(item.asset) : '',
  snapshot_source: item.snapshot_source || 'manually',
  snapshot_value: item.snapshot_value != null ? String(item.snapshot_value) : '',
  snapshot_quantity:
    item.snapshot_quantity != null && item.snapshot_quantity !== ''
      ? String(item.snapshot_quantity)
      : '1',
  market_unitary_value:
    item.market_unitary_value != null ? String(item.market_unitary_value) : '',
  market_source_json: marketSourceToString(item.market_source),
});

/** @returns {object[] | null} API items or null if validation failed */
export const buildItemsPayloadFromRows = (rows, assets = null) => {
  const assetsById =
    assets && assets.length > 0 ? new Map(assets.map((a) => [a.id, a])) : null;
  const out = [];
  const seen = new Set();
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const assetId = r.asset?.trim() ? Number(r.asset) : null;
    if (!assetId) {
      toast.error(`Row ${i + 1}: select an asset.`);
      return null;
    }
    if (seen.has(assetId)) {
      toast.error('Each asset can only appear once in the snapshot.');
      return null;
    }
    seen.add(assetId);
    const sv = parseDecimal(r.snapshot_value);
    if (sv === null) {
      toast.error(`Row ${i + 1}: invalid snapshot value.`);
      return null;
    }
    const sq = parseDecimal(r.snapshot_quantity);
    if (r.snapshot_quantity?.trim() !== '' && sq === null) {
      toast.error(`Row ${i + 1}: invalid quantity.`);
      return null;
    }
    const rowAsset = assetsById?.get(assetId);
    const marketOn = isAssetMarketFieldsApplicable(rowAsset);
    let muv = null;
    let market_source = null;
    if (marketOn) {
      muv = parseDecimal(r.market_unitary_value);
      if (r.market_unitary_value?.trim() !== '' && muv === null) {
        toast.error(`Row ${i + 1}: invalid market unit value.`);
        return null;
      }
      const rawJson = (r.market_source_json || '').trim();
      if (rawJson) {
        try {
          market_source = JSON.parse(rawJson);
          if (market_source != null && typeof market_source !== 'object') {
            toast.error(`Row ${i + 1}: market_source must be a JSON object or array.`);
            return null;
          }
        } catch (_) {
          toast.error(`Row ${i + 1}: market_source is not valid JSON.`);
          return null;
        }
      }
    }
    out.push({
      asset: assetId,
      snapshot_source: r.snapshot_source?.trim() || 'manually',
      snapshot_value: sv,
      snapshot_quantity: sq != null ? sq : null,
      market_unitary_value: muv != null ? muv : null,
      market_source,
    });
  }
  if (out.length < 1) {
    toast.error('Add at least one line to the snapshot.');
    return null;
  }
  return out;
};

/**
 * Like buildItemsPayloadFromRows but skips rows with no asset selected.
 * Rows with an asset must pass the same field validation.
 * Returns [] if no complete rows (caller may merge multiple tables).
 * @returns {object[] | null}
 */
export const buildItemsPayloadFromRowsAllowEmptyLines = (
  rows,
  sectionLabel = 'Items',
  assets = null
) => {
  const assetsById =
    assets && assets.length > 0 ? new Map(assets.map((a) => [a.id, a])) : null;
  const out = [];
  const seen = new Set();
  const prefix = (i) => `${sectionLabel} row ${i + 1}:`;

  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i];
    const assetId = r.asset?.trim() ? Number(r.asset) : null;
    if (!assetId) {
      continue;
    }
    if (seen.has(assetId)) {
      toast.error(`${sectionLabel}: each asset can only appear once in this section.`);
      return null;
    }
    seen.add(assetId);
    const sv = parseDecimal(r.snapshot_value);
    if (sv === null) {
      toast.error(`${prefix(i)} invalid snapshot value.`);
      return null;
    }
    const sq = parseDecimal(r.snapshot_quantity);
    if (r.snapshot_quantity?.trim() !== '' && sq === null) {
      toast.error(`${prefix(i)} invalid quantity.`);
      return null;
    }
    const rowAsset = assetsById?.get(assetId);
    const marketOn = isAssetMarketFieldsApplicable(rowAsset);
    let muv = null;
    let market_source = null;
    if (marketOn) {
      muv = parseDecimal(r.market_unitary_value);
      if (r.market_unitary_value?.trim() !== '' && muv === null) {
        toast.error(`${prefix(i)} invalid market unit value.`);
        return null;
      }
      const rawJson = (r.market_source_json || '').trim();
      if (rawJson) {
        try {
          market_source = JSON.parse(rawJson);
          if (market_source != null && typeof market_source !== 'object') {
            toast.error(`${prefix(i)} market_source must be a JSON object or array.`);
            return null;
          }
        } catch (_) {
          toast.error(`${prefix(i)} market_source is not valid JSON.`);
          return null;
        }
      }
    }
    out.push({
      asset: assetId,
      snapshot_source: r.snapshot_source?.trim() || 'manually',
      snapshot_value: sv,
      snapshot_quantity: sq != null ? sq : null,
      market_unitary_value: muv != null ? muv : null,
      market_source,
    });
  }
  return out;
};
